import { __ } from '@wordpress/i18n';
import React from 'react';
import { createRoot } from 'react-dom/client';
import ReactPlayer from 'react-player';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

const videoComponent = () => {
	const videoComponents = document.querySelectorAll(
		'.component-video:not(.component-lightbox__button):not([data-lightbox-content])'
	);
	const buttonLinks = document.querySelectorAll(
		'.block-card-image-content .wp-block-button__link:not(.component-video), .block-hero-display .wp-block-button__link:not(.component-video)'
	);
	let videoLightbox;
	let videoLightboxContent;
	let root;

	const isPlayableVideoURL = (url) => {
		if (!url || typeof ReactPlayer.canPlay !== 'function') {
			return false;
		}

		try {
			return ReactPlayer.canPlay(url);
		} catch (error) {
			return false;
		}
	};

	const getVideoURL = (element) =>
		element.getAttribute('data-embed-url') || element.getAttribute('href');

	const isExplicitVideoTrigger = (element) =>
		element.classList.contains('component-video') ||
		element.hasAttribute('data-embed-url');

	const initializeVideoLightbox = (element, isButtonLink = false) => {
		const videoLightboxFigure = element;
		const isDirectTrigger =
			isButtonLink ||
			videoLightboxFigure.matches('a, button, .wp-block-button__link');

		if (isDirectTrigger) {
			videoLightboxFigure.addEventListener('click', openVideoLightbox);
		} else {
			videoLightboxFigure.classList.add('component-video__button');
			videoLightboxFigure
				.querySelector('.component-video__play-button')
				?.addEventListener('click', openVideoLightbox);
			videoLightboxFigure.addEventListener('click', openVideoLightbox);
			videoLightboxFigure.setAttribute(
				'aria-label',
				__('Open Video in Lightbox', 'propel')
			);
		}
	};

	// open lightbox
	const openVideoLightbox = (e) => {
		const lightboxVideoComponent =
			e.currentTarget.closest('.component-video') || e.currentTarget;
		const videoURL = getVideoURL(lightboxVideoComponent);

		if (
			!isExplicitVideoTrigger(lightboxVideoComponent) &&
			!isPlayableVideoURL(videoURL)
		) {
			return;
		}

		e.preventDefault();

		if (!videoLightbox) {
			createVideoLightbox();
		}

		const lightboxVideoContent = videoLightbox.querySelector(
			'.component-video-lightbox__content'
		);

		videoLightbox.classList.add('active');

		disableBodyScroll(videoLightbox, {
			reserveScrollBarGap: true,
		});

		setTimeout(() => {
			videoLightbox.classList.add('visible');
		}, 1);

		document.addEventListener('keydown', keydown);

		if (videoURL) {
			root = createRoot(lightboxVideoContent);

			root.render(
				<ReactPlayer
					className="component-video-lightbox__video"
					url={videoURL}
					playing
					height="100%"
					width="100%"
					controls={true}
				/>
			);
		}
	};

	const closeVideoLightbox = (e) => {
		e.preventDefault();

		videoLightbox.classList.remove('visible');

		setTimeout(() => {
			videoLightbox.classList.remove('active');
			clearAllBodyScrollLocks();
			videoLightboxContent.innerHTML = '';
		}, 400);

		if (root) root.unmount();

		document.removeEventListener('keydown', keydown);
	};

	const keydown = (e) => {
		if (e.key === 'Escape') {
			closeVideoLightbox(e);
		}
	};

	// create lightbox
	const createVideoLightbox = () => {
		videoLightbox = document.createElement('div');
		videoLightbox.classList.add('bg-dark', 'component-video-lightbox');
		videoLightbox.setAttribute('role', 'dialog');
		videoLightbox.setAttribute('aria-modal', 'true');

		const videoLightboxContainer = document.createElement('div');
		videoLightboxContainer.classList.add(
			'component-video-lightbox__container'
		);

		videoLightboxContent = document.createElement('div');
		videoLightboxContent.classList.add('component-video-lightbox__content');

		const videoLightboxCloseOverlay = document.createElement('button');
		videoLightboxCloseOverlay.classList.add(
			'component-video-lightbox__close-button-overlay'
		);
		videoLightboxCloseOverlay.setAttribute('role', 'button');
		videoLightboxCloseOverlay.setAttribute(
			'aria-label',
			__('Close Lightbox', 'propel')
		);
		videoLightboxCloseOverlay.addEventListener('click', closeVideoLightbox);

		const videoLightboxCloseButton = document.createElement('span');
		videoLightboxCloseButton.classList.add(
			'component-video-lightbox__close-button'
		);
		videoLightboxCloseButton.addEventListener('click', closeVideoLightbox);

		videoLightboxCloseOverlay.appendChild(videoLightboxCloseButton);
		videoLightboxContainer.appendChild(videoLightboxContent);
		videoLightbox.appendChild(videoLightboxContainer);
		videoLightbox.appendChild(videoLightboxCloseOverlay);
		document.body.appendChild(videoLightbox);
	};

	// Loop through each video component and button link to initialize the lightbox
	if (videoComponents.length > 0) {
		videoComponents.forEach((video) => {
			initializeVideoLightbox(video);
		});
	}

	if (buttonLinks.length > 0) {
		buttonLinks.forEach((button) => {
			if (isPlayableVideoURL(button.getAttribute('href'))) {
				initializeVideoLightbox(button, true);
			}
		});
	}
};

videoComponent();

export default videoComponent;
